/**
 * Global upload progress – background video upload with progress bar.
 * startUpload({ exercise, weight, reps }, file) compresses (if needed), uploads, then creates workout.
 */
import { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { submitLift } from '../../domain/usecases/workouts';
import { createChallengeSubmission } from '../../domain/usecases/challenges';
import { uploadLiftVideoWithProgress, uploadChallengeVideoWithProgress, safeDeleteUploadByUrl } from '../../data/firebase/storage';
import { compressVideo } from '../../shared/utils/videoCompress';

const UploadContext = createContext(null);

export function UploadProvider({ children }) {
  const { userId } = useAuth();
  const [progress, setProgress] = useState(null); // null | { percent, status, type?: 'lift'|'challenge', error?: string }

  const startUpload = useCallback(
    async (formData, file) => {
      if (!userId || !file) return Promise.reject(new Error('Missing user or file'));
      setProgress({ percent: 0, status: 'compressing', type: 'lift' });
      let blob = file;
      try {
        const compressed = await compressVideo(file, (percent) => {
          setProgress((p) => (p && p.status === 'compressing' ? { ...p, percent, status: 'compressing' } : p));
        });
        if (compressed) blob = compressed;
      } catch (e) {
        console.warn('Compression failed, uploading original', e);
      }
      setProgress((p) => (p ? { ...p, status: 'uploading', percent: 0 } : null));
      const fileToUpload = blob instanceof File ? blob : new File([blob], file.name || 'video.webm', { type: blob.type || 'video/webm' });
      let videoUrl = '';
      try {
        videoUrl = await uploadLiftVideoWithProgress(userId, fileToUpload, (percent) => {
          setProgress((p) => (p ? { ...p, percent, status: 'uploading' } : null));
        });
      } catch (err) {
        setProgress((p) => (p ? { ...p, status: 'error', error: err.message } : null));
        setTimeout(() => setProgress(null), 5000);
        throw err;
      }
      setProgress((p) => (p ? { ...p, status: 'creating', percent: 100 } : null));
      try {
        await submitLift(userId, {
          exercise: formData.exercise,
          weight: formData.weight,
          reps: formData.reps,
          videoUrl,
          showInFeed: formData.showInFeed === true,
        });
      } catch (err) {
        // Orphan cleanup: the video uploaded but the workout doc failed to create.
        // Delete the upload so the user can retry cleanly and we don't pay for dead storage.
        await safeDeleteUploadByUrl(videoUrl);
        setProgress((p) => (p ? { ...p, status: 'error', error: err.message } : null));
        setTimeout(() => setProgress(null), 5000);
        throw err;
      }
      setProgress(null);
    },
    [userId]
  );

  const startChallengeUpload = useCallback(
    async (challengeId, file) => {
      if (!userId || !file || !challengeId) return Promise.reject(new Error('Missing user, file, or challenge'));
      setProgress({ percent: 0, status: 'compressing', type: 'challenge' });
      let blob = file;
      try {
        const compressed = await compressVideo(file, (percent) => {
          setProgress((p) => (p && p.status === 'compressing' ? { ...p, percent, status: 'compressing' } : p));
        });
        if (compressed) blob = compressed;
      } catch (e) {
        console.warn('Compression failed, uploading original', e);
      }
      setProgress((p) => (p ? { ...p, status: 'uploading', percent: 0 } : null));
      const fileToUpload = blob instanceof File ? blob : new File([blob], file.name || 'video.webm', { type: blob.type || 'video/webm' });
      let videoUrl = '';
      try {
        videoUrl = await uploadChallengeVideoWithProgress(challengeId, userId, fileToUpload, (percent) => {
          setProgress((p) => (p ? { ...p, percent, status: 'uploading' } : null));
        });
      } catch (err) {
        setProgress((p) => (p ? { ...p, status: 'error', error: err.message } : null));
        setTimeout(() => setProgress(null), 5000);
        throw err;
      }
      setProgress((p) => (p ? { ...p, status: 'creating', percent: 100 } : null));
      try {
        await createChallengeSubmission(challengeId, userId, videoUrl);
      } catch (err) {
        // Orphan cleanup: video uploaded but submission doc failed (e.g. duplicate, rules,
        // network). Remove the dead video so the user can retry and we don't leak storage.
        await safeDeleteUploadByUrl(videoUrl);
        const message = err?.code === 'already-exists'
          ? 'You already submitted to this challenge.'
          : (err?.message || 'Submission failed.');
        setProgress((p) => (p ? { ...p, status: 'error', error: message } : null));
        setTimeout(() => setProgress(null), 5000);
        const outErr = new Error(message);
        outErr.code = err?.code;
        throw outErr;
      }
      setProgress(null);
    },
    [userId]
  );

  const clear = useCallback(() => setProgress(null), []);

  const value = { progress, startUpload, startChallengeUpload, clear };
  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
}

export function useUpload() {
  const ctx = useContext(UploadContext);
  return ctx || { progress: null, startUpload: async () => {}, startChallengeUpload: async () => {}, clear: () => {} };
}
